// src/components/products/ProductEligibilityForm.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import {
  fetchProduct,
  addEligibility,
  updateEligibility,
  clearProductError,
  clearProductSuccess,
} from '../../store/actions/productActions';
import LoadingSpinner from '../common/LoadingSpinner';
import Alert from '../common/Alert';
import ConfirmDialog from '../common/ConfirmDialog';

const ProductEligibilityForm = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id, eligibilityId } = useParams();
  const { selectedProduct: product, eligibilityCriteria, loading, error, success } = useSelector(
    (state) => state.products
  );

  const [formData, setFormData] = useState({
    criterion: '',
    order: 0,
  });

  const [errors, setErrors] = useState({});
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isEditMode = !!eligibilityId;

  // Styles object
  const styles = {
    container: {
      maxWidth: '800px',
      margin: '0 auto',
      padding: '2rem 1rem',
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '2rem',
    },
    headerLeft: {
      display: 'flex',
      alignItems: 'center',
      gap: '1rem',
    },
    backButton: {
      padding: '0.5rem',
      backgroundColor: '#f3f4f6',
      border: 'none',
      borderRadius: '0.375rem',
      cursor: 'pointer',
      color: '#374151',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'background-color 0.2s',
    },
    backButtonHover: {
      backgroundColor: '#e5e7eb',
    },
    title: {
      fontSize: '1.5rem',
      fontWeight: 700,
      color: '#111827',
      margin: 0,
    },
    cancelButton: {
      padding: '0.5rem 1rem',
      backgroundColor: '#ffffff',
      color: '#374151',
      border: '1px solid #d1d5db',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
      transition: 'all 0.2s',
    },
    cancelButtonHover: {
      backgroundColor: '#f9fafb',
    },
    productInfo: {
      marginBottom: '2rem',
      padding: '1rem',
      backgroundColor: '#f9fafb',
      borderRadius: '0.5rem',
    },
    productName: {
      fontSize: '1rem',
      fontWeight: 600,
      color: '#111827',
    },
    form: {
      backgroundColor: '#ffffff',
      borderRadius: '0.5rem',
      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
      padding: '2rem',
    },
    fieldGroup: {
      marginBottom: '1.5rem',
    },
    label: {
      display: 'block',
      fontSize: '0.875rem',
      fontWeight: 500,
      color: '#374151',
      marginBottom: '0.5rem',
    },
    input: {
      width: '100%',
      padding: '0.625rem',
      fontSize: '0.875rem',
      border: '1px solid #d1d5db',
      borderRadius: '0.375rem',
      outline: 'none',
      transition: 'border-color 0.2s, boxShadow 0.2s',
    },
    textarea: {
      width: '100%',
      padding: '0.625rem',
      fontSize: '0.875rem',
      border: '1px solid #d1d5db',
      borderRadius: '0.375rem',
      outline: 'none',
      minHeight: '100px',
      resize: 'vertical',
    },
    inputError: {
      borderColor: '#dc2626',
    },
    errorText: {
      marginTop: '0.25rem',
      fontSize: '0.75rem',
      color: '#dc2626',
    },
    helperText: {
      marginTop: '0.25rem',
      fontSize: '0.75rem',
      color: '#6b7280',
    },
    buttonGroup: {
      display: 'flex',
      justifyContent: 'flex-end',
      gap: '1rem',
      marginTop: '2rem',
    },
    submitButton: {
      padding: '0.75rem 1.5rem',
      backgroundColor: '#2563eb',
      color: '#ffffff',
      border: 'none',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
      transition: 'background-color 0.2s',
    },
    submitButtonHover: {
      backgroundColor: '#1d4ed8',
    },
    submitButtonDisabled: {
      backgroundColor: '#9ca3af',
      cursor: 'not-allowed',
    },
    icon: {
      width: '1.25rem',
      height: '1.25rem',
    },
  };

  useEffect(() => {
    if (id) {
      dispatch(fetchProduct(id));
    }
  }, [dispatch, id]);

  useEffect(() => {
    if (isEditMode && eligibilityCriteria) {
      const criterion = eligibilityCriteria.find(c => c.id === eligibilityId);
      if (criterion) {
        setFormData({
          criterion: criterion.criterion || '',
          order: criterion.order || 0,
        });
      }
    }
  }, [isEditMode, eligibilityCriteria, eligibilityId]);

  // Get next order number for new criteria
  useEffect(() => {
    if (!isEditMode && eligibilityCriteria) {
      const maxOrder = eligibilityCriteria.reduce((max, c) => Math.max(max, c?.order || 0), 0);
      setFormData(prev => ({ ...prev, order: maxOrder + 1 }));
    }
  }, [isEditMode, eligibilityCriteria]);

  // Cleanup
  useEffect(() => {
    return () => {
      dispatch(clearProductError());
      dispatch(clearProductSuccess());
    };
  }, [dispatch]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.criterion.trim()) {
      newErrors.criterion = 'Le critère est requis';
    }

    if (formData.order < 0) {
      newErrors.order = "L'ordre ne peut pas être négatif";
    }

    return newErrors;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'order' ? parseInt(value) || 0 : value,
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validateForm();
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      return;
    }

    setIsSubmitting(true);

    try {
      if (isEditMode) {
        await dispatch(updateEligibility(id, eligibilityId, formData));
      } else {
        await dispatch(addEligibility(id, formData));
      }
      navigate(`/admin/products/${id}/eligibility`);
    } catch (error) {
      console.error('Form submission failed:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (formData.criterion) {
      setShowCancelConfirm(true);
    } else {
      navigate(`/admin/products/${id}/eligibility`);
    }
  };

  if (loading && isEditMode) {
    return <LoadingSpinner fullScreen />;
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <button
            onClick={() => navigate(`/admin/products/${id}/eligibility`)}
            style={styles.backButton}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = styles.backButtonHover.backgroundColor;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = styles.backButton.backgroundColor;
            }}
          >
            <svg style={styles.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
          </button>
          <h1 style={styles.title}>
            {isEditMode ? 'Modifier le critère' : 'Nouveau critère d\'éligibilité'}
          </h1>
        </div>
        <button
          onClick={handleCancel}
          style={styles.cancelButton}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = styles.cancelButtonHover.backgroundColor;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = styles.cancelButton.backgroundColor;
          }}
        >
          Annuler
        </button>
      </div>

      {product && (
        <div style={styles.productInfo}>
          <div style={styles.productName}>
            Produit: {product.name}
          </div>
        </div>
      )}

      {error && (
        <Alert
          type="error"
          message={error}
          onClose={() => dispatch(clearProductError())}
        />
      )}

      {success && (
        <Alert
          type="success"
          message={success}
          onClose={() => dispatch(clearProductSuccess())}
        />
      )}

      <form onSubmit={handleSubmit} style={styles.form}>
        <div style={styles.fieldGroup}>
          <label style={styles.label} htmlFor="criterion">
            Critère d'éligibilité *
          </label>
          <textarea
            id="criterion"
            name="criterion"
            value={formData.criterion}
            onChange={handleChange}
            style={{
              ...styles.textarea,
              ...(errors.criterion ? styles.inputError : {}),
            }}
            placeholder="Ex: Être âgé de 18 à 65 ans, Résider au Sénégal, etc."
            rows="4"
          />
          {errors.criterion && (
            <div style={styles.errorText}>{errors.criterion}</div>
          )}
          <div style={styles.helperText}>
            Décrivez les conditions requises pour être éligible à ce produit
          </div>
        </div>

        <div style={styles.fieldGroup}>
          <label style={styles.label} htmlFor="order">
            Ordre d'affichage
          </label>
          <input
            type="number"
            id="order"
            name="order"
            value={formData.order}
            onChange={handleChange}
            min="0"
            style={{
              ...styles.input,
              ...(errors.order ? styles.inputError : {}),
            }}
          />
          {errors.order && (
            <div style={styles.errorText}>{errors.order}</div>
          )}
          <div style={styles.helperText}>
            Les critères sont triés par ordre croissant
          </div>
        </div>

        <div style={styles.buttonGroup}>
          <button
            type="button"
            onClick={handleCancel}
            style={styles.cancelButton}
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            style={{
              ...styles.submitButton,
              ...(isSubmitting ? styles.submitButtonDisabled : {}),
            }}
            onMouseEnter={(e) => {
              if (!isSubmitting) {
                e.currentTarget.style.backgroundColor = styles.submitButtonHover.backgroundColor;
              }
            }}
            onMouseLeave={(e) => {
              if (!isSubmitting) {
                e.currentTarget.style.backgroundColor = styles.submitButton.backgroundColor;
              }
            }}
          >
            {isSubmitting 
              ? (isEditMode ? 'Modification...' : 'Ajout...') 
              : (isEditMode ? 'Mettre à jour' : 'Ajouter')}
          </button>
        </div>
      </form>

      <ConfirmDialog
        isOpen={showCancelConfirm}
        onClose={() => setShowCancelConfirm(false)}
        onConfirm={() => navigate(`/admin/products/${id}/eligibility`)}
        title="Annuler les modifications"
        message="Vous avez des modifications non enregistrées. Êtes-vous sûr de vouloir quitter ?"
        confirmText="Quitter"
        cancelText="Continuer"
        type="warning"
      />
    </div>
  );
};

export default ProductEligibilityForm;